import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useShiftPlan } from '../../../context/ShiftPlanContext';
import { useTeam } from '../../../context/TeamContext';
import { useAuth } from '../../../context/AuthContext';
import ShiftPlanCalendar from './ShiftPlanCalendar';
import { SHIFT_TYPES } from '../../../data/nurseAppModels';
import { collection, query, where, getDocs, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';

const ReviewShiftPlanPage = () => {
  const navigate = useNavigate();
  const { planId } = useParams();
  const { getPlanById, getPlanRequests, approvePlan, rejectPlan } = useShiftPlan();
  const { currentTeam } = useTeam();
  const { currentUser } = useAuth();
  
  const [plan, setPlan] = useState(null);
  const [requests, setRequests] = useState([]);
  const [approvedShifts, setApprovedShifts] = useState([]);
  const [teamMembersWithProfile, setTeamMembersWithProfile] = useState([]);
  const [approverName, setApproverName] = useState(null);
  const [pendingSwapRequests, setPendingSwapRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [error, setError] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Check if user can approve (must be team creator/leaderId)
  const canApprove = React.useMemo(() => {
    if (!currentTeam || !currentUser) return false;
    return currentTeam.leaderId === currentUser.uid;
  }, [currentTeam, currentUser]);

  // Fetch team members with profile data
  useEffect(() => {
    const fetchTeamMembersWithProfile = async () => {
      if (!currentTeam || !currentTeam.members) return;

      try {
        const membersWithProfile = await Promise.all(
          currentTeam.members.map(async (member) => {
            try {
              const userRef = doc(db, 'users', member.uid);
              const userSnap = await getDoc(userRef);
              const userData = userSnap.exists() ? userSnap.data() : null;
              
              return {
                ...member,
                photoURL: userData?.photoURL || null,
                displayName: userData?.displayName || member.name || 'Unknown'
              };
            } catch (err) {
              console.error(`Error fetching user ${member.uid}:`, err);
              return {
                ...member,
                photoURL: null,
                displayName: member.name || 'Unknown'
              };
            }
          })
        );
        setTeamMembersWithProfile(membersWithProfile);
      } catch (err) {
        console.error('Error fetching team members:', err);
      }
    };

    if (currentTeam) {
      fetchTeamMembersWithProfile();
    }
  }, [currentTeam]);

  // Load plan and requests
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const planResult = await getPlanById(planId);
        
        if (planResult.success) {
          const planData = planResult.plan;
          setPlan(planData);
          
          // Load requests
          const requestsData = await getPlanRequests(planId);
          setRequests(requestsData);
          
          // If plan is approved, load shifts from shifts collection
          if (planData.status === 'APPROVED') {
            try {
              const shiftsSnapshot = await getDocs(
                query(
                  collection(db, 'shifts'),
                  where('planId', '==', planId)
                )
              );
              
              const shifts = shiftsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
              }));
              
              setApprovedShifts(shifts);
            } catch (shiftErr) {
              console.error('Error loading approved shifts:', shiftErr);
            }

            // Fetch approver's name
            if (planData.approvedBy) {
              try {
                const approverRef = doc(db, 'users', planData.approvedBy);
                const approverSnap = await getDoc(approverRef);
                if (approverSnap.exists()) {
                  const approverData = approverSnap.data();
                  setApproverName(approverData.displayName || approverData.name || planData.approvedBy);
                } else {
                  setApproverName(planData.approvedBy);
                }
              } catch (approverErr) {
                console.error('Error fetching approver name:', approverErr);
                setApproverName(planData.approvedBy);
              }
            }
          }
        } else {
          setError(planResult.error || 'ไม่พบแผน');
        }
      } catch (err) {
        console.error('Error loading plan:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (planId && currentUser) {
      loadData();
    }
  }, [planId, currentUser, getPlanById, getPlanRequests]);

  // Fetch pending swap requests for this plan (to show in calendar)
  useEffect(() => {
    if (!planId || !currentTeam || !plan) return;

    // Only fetch if plan is approved
    if (plan.status !== 'APPROVED') {
      setPendingSwapRequests([]);
      return;
    }

    // Fetch all swap requests for this plan (PENDING and MEMBER_APPROVED)
    const unsubscribe = onSnapshot(
      query(
        collection(db, 'shiftSwaps'),
        where('planId', '==', planId)
      ),
      (snapshot) => {
        const swaps = snapshot.docs
          .map(docSnap => ({
            id: docSnap.id,
            ...docSnap.data()
          }))
          .filter(swap => 
            swap.status === 'PENDING' || 
            swap.status === 'MEMBER_APPROVED'
          );
        setPendingSwapRequests(swaps);
        console.log('[ReviewShiftPlanPage] Pending swap requests:', swaps.length);
      },
      (err) => {
        console.error('[ReviewShiftPlanPage] Error fetching swap requests:', err);
      }
    );

    return () => unsubscribe();
  }, [planId, currentTeam, plan]);

  // Combine all submitted shifts for calendar display
  const allSubmittedShifts = React.useMemo(() => {
    // If plan is approved, use shifts from shifts collection
    if (plan?.status === 'APPROVED' && approvedShifts.length > 0) {
      return approvedShifts.map(shift => {
        // Convert shift to calendar format
        const startDate = shift.startTime?.toDate ? shift.startTime.toDate() : 
                         shift.startTime?.seconds ? new Date(shift.startTime.seconds * 1000) : 
                         new Date();
        const dateStr = startDate.toISOString().split('T')[0]; // YYYY-MM-DD
        
        return {
          date: dateStr,
          shiftType: shift.shiftType,
          userId: shift.assignedTo,
          userName: shift.assignedToName,
          notes: shift.notes || ''
        };
      });
    }
    
    // Otherwise, use submitted requests
    const shifts = [];
    requests.forEach(request => {
      if (request.status === 'SUBMITTED' && request.requestedShifts) {
        request.requestedShifts.forEach(shift => {
          shifts.push({
            ...shift,
            userId: request.userId,
            userName: request.userName
          });
        });
      }
    });
    return shifts;
  }, [requests, plan, approvedShifts]);

  // Handle approve
  const handleApprove = async () => {
    if (!window.confirm('คุณแน่ใจหรือไม่ที่จะอนุมัติแผนนี้? การอนุมัติจะสร้างเวรทั้งหมดตามที่สมาชิกกรอกไว้')) {
      return;
    }

    setApproving(true);
    setError(null);

    try {
      const result = await approvePlan(planId);

      if (result.success) {
        alert('อนุมัติแผนสำเร็จ! เวรทั้งหมดถูกสร้างแล้ว');
        navigate('/nurse/shifts/plans');
      } else {
        setError(result.error || 'เกิดข้อผิดพลาดในการอนุมัติ');
      }
    } catch (err) {
      console.error('Error approving plan:', err);
      setError(err.message || 'เกิดข้อผิดพลาดในการอนุมัติ');
    } finally {
      setApproving(false);
    }
  };

  // Handle reject
  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('กรุณากรอกเหตุผลในการปฏิเสธ');
      return;
    }

    if (!window.confirm('คุณแน่ใจหรือไม่ที่จะปฏิเสธแผนนี้?')) {
      return;
    }

    setRejecting(true);
    setError(null);

    try {
      const result = await rejectPlan(planId, rejectionReason);

      if (result.success) {
        alert('ปฏิเสธแผนสำเร็จ');
        navigate('/nurse/shifts/plans');
      } else {
        setError(result.error || 'เกิดข้อผิดพลาดในการปฏิเสธ');
      }
    } catch (err) {
      console.error('Error rejecting plan:', err);
      setError(err.message || 'เกิดข้อผิดพลาดในการปฏิเสธ');
    } finally {
      setRejecting(false);
    }
  };

  // Get shift type label
  const getShiftTypeLabel = (type) => {
    switch (type) {
      case SHIFT_TYPES.MORNING: return 'เช้า';
      case SHIFT_TYPES.AFTERNOON: return 'บ่าย';
      case SHIFT_TYPES.NIGHT: return 'ดึก';
      default: return type;
    }
  };

  // Count shifts by type
  const shiftsByType = allSubmittedShifts.reduce((acc, shift) => {
    acc[shift.shiftType] = (acc[shift.shiftType] || 0) + 1;
    return acc;
  }, {});

  // Count shifts by user
  const shiftsByUser = allSubmittedShifts.reduce((acc, shift) => {
    if (!acc[shift.userId]) {
      acc[shift.userId] = {
        userName: shift.userName,
        count: 0,
        byType: {}
      };
    }
    acc[shift.userId].count++;
    acc[shift.userId].byType[shift.shiftType] = (acc[shift.userId].byType[shift.shiftType] || 0) + 1;
    return acc;
  }, {});

  // Get submitted members count
  const submittedMembers = React.useMemo(() => {
    if (plan?.status === 'APPROVED' && approvedShifts.length > 0) {
      // If approved, count unique users from shifts
      const uniqueUsers = new Set();
      approvedShifts.forEach(shift => {
        if (shift.assignedTo) {
          uniqueUsers.add(shift.assignedTo);
        }
      });
      return Array.from(uniqueUsers).map(userId => {
        const shift = approvedShifts.find(s => s.assignedTo === userId);
        return {
          userId,
          userName: shift?.assignedToName || 'Unknown',
          status: 'APPROVED'
        };
      });
    }
    return requests.filter(r => r.status === 'SUBMITTED' || r.status === 'APPROVED');
  }, [requests, plan, approvedShifts]);
  
  const totalMembers = currentTeam?.members?.length || 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 mb-4">ไม่พบแผน</p>
          <button
            onClick={() => navigate('/nurse/shifts/plans')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            กลับไปหน้าแผนเวร
          </button>
        </div>
      </div>
    );
  }

  // Check if plan is approved (read-only mode)
  const isApproved = plan.status === 'APPROVED';
  const isReadOnly = isApproved;

  // Check if user can approve (only for non-approved plans)
  if (!isApproved && !canApprove) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 mb-2">คุณไม่มีสิทธิ์อนุมัติแผน</p>
          <p className="text-sm text-gray-500 mb-4">เฉพาะหัวหน้าทีมที่สร้างทีมเท่านั้น</p>
          <button
            onClick={() => navigate('/nurse/shifts/plans')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            กลับไปหน้าแผนเวร
          </button>
        </div>
      </div>
    );
  }

  // Check if plan can be approved (only for SUBMITTED status)
  if (!isApproved && plan.status !== 'SUBMITTED') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 mb-2">แผนนี้ไม่อยู่ในสถานะที่สามารถอนุมัติได้</p>
          <p className="text-sm text-gray-500 mb-4">สถานะปัจจุบัน: {plan.status}</p>
          <button
            onClick={() => navigate('/nurse/shifts/plans')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            กลับไปหน้าแผนเวร
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/nurse/shifts/plans')}
            className="text-blue-600 hover:text-blue-700 mb-4 flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            กลับไปหน้าแผนเวร
          </button>
          <h1 className="text-2xl font-bold text-gray-800">
            {isApproved ? 'ดูแผนที่อนุมัติแล้ว' : 'Review และอนุมัติแผน'}
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            แผน: <span className="font-medium">{plan.planName || plan.planNumber}</span> (Version {plan.version})
            {isApproved && (
              <span className="ml-2 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                อนุมัติแล้ว
              </span>
            )}
          </p>
        </div>

        {/* Plan Info Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <p className="text-sm text-gray-500">เลขแผน</p>
              <p className="font-medium text-gray-800">{plan.planNumber}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Version</p>
              <p className="font-medium text-gray-800">{plan.version}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">สถานะ</p>
              <p className="font-medium text-gray-800">
                <span className={`px-2 py-1 rounded text-sm ${
                  plan.status === 'APPROVED' ? 'bg-green-100 text-green-700' :
                  plan.status === 'SUBMITTED' ? 'bg-yellow-100 text-yellow-700' :
                  plan.status === 'DRAFT' ? 'bg-gray-100 text-gray-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {plan.status === 'APPROVED' ? 'อนุมัติแล้ว' :
                   plan.status === 'SUBMITTED' ? 'ส่งแล้ว' :
                   plan.status === 'DRAFT' ? 'ร่าง' :
                   plan.status}
                </span>
              </p>
            </div>
          </div>

          {/* Submission Status */}
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm font-medium text-blue-900 mb-2">
              สถานะการส่งเวร: {submittedMembers.length} / {totalMembers} คน
            </p>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all"
                style={{ width: `${totalMembers > 0 ? (submittedMembers.length / totalMembers) * 100 : 0}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Calendar View */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">เวรที่สมาชิกกรอกแล้ว</h2>
          <ShiftPlanCalendar
            plan={plan}
            selectedShifts={allSubmittedShifts}
            readOnly={isApproved}
            allowViewDetails={isApproved}
            pendingSwapRequests={pendingSwapRequests}
            teamMembersWithProfile={teamMembersWithProfile}
          />
        </div>

        {/* Summary */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">สรุปเวร</h2>
          
          {/* Summary by type */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-700">
                {shiftsByType[SHIFT_TYPES.MORNING] || 0}
              </p>
              <p className="text-sm text-green-600">เวรเช้า</p>
            </div>
            <div className="text-center p-3 bg-yellow-50 rounded-lg">
              <p className="text-2xl font-bold text-yellow-700">
                {shiftsByType[SHIFT_TYPES.AFTERNOON] || 0}
              </p>
              <p className="text-sm text-yellow-600">เวรบ่าย</p>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-700">
                {shiftsByType[SHIFT_TYPES.NIGHT] || 0}
              </p>
              <p className="text-sm text-blue-600">เวรดึก</p>
            </div>
          </div>

          {/* Summary by user */}
          <div>
            <h3 className="text-md font-medium text-gray-700 mb-3">เวรตามสมาชิก</h3>
            <div className="space-y-2">
              {Object.entries(shiftsByUser).map(([userId, data]) => (
                <div key={userId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-800">{data.userName}</p>
                    <p className="text-sm text-gray-600">
                      รวม {data.count} เวร (
                      {data.byType[SHIFT_TYPES.MORNING] || 0} เช้า, 
                      {data.byType[SHIFT_TYPES.AFTERNOON] || 0} บ่าย, 
                      {data.byType[SHIFT_TYPES.NIGHT] || 0} ดึก)
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Submitted Members List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            {isApproved ? 'สมาชิกที่ได้รับมอบหมายเวร' : 'สมาชิกที่ส่งเวรแล้ว'}
          </h2>
          {submittedMembers.length === 0 ? (
            <p className="text-gray-500 text-center py-4">
              {isApproved ? 'ยังไม่มีสมาชิกได้รับมอบหมายเวร' : 'ยังไม่มีสมาชิกส่งเวร'}
            </p>
          ) : (
            <div className="space-y-2">
              {submittedMembers.map((member, index) => {
                // Calculate shift count for this user
                const userShifts = isApproved 
                  ? approvedShifts.filter(s => s.assignedTo === member.userId)
                  : member.requestedShifts || [];
                
                return (
                  <div key={member.id || member.userId || index} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                    <div>
                      <p className="font-medium text-green-800">{member.userName}</p>
                      {isApproved ? (
                        <p className="text-sm text-green-600">
                          อนุมัติเมื่อ: {plan.approvedAt?.toDate?.().toLocaleString('th-TH') || 
                                       plan.approvedAt?.seconds ? new Date(plan.approvedAt.seconds * 1000).toLocaleString('th-TH') :
                                       'N/A'}
                        </p>
                      ) : (
                        <p className="text-sm text-green-600">
                          ส่งเมื่อ: {member.submittedAt?.toDate?.().toLocaleString('th-TH') || 'N/A'}
                        </p>
                      )}
                    </div>
                    <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-700">
                      {userShifts.length} เวร
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Rejection Reason (if rejecting) - Only show if not approved */}
        {!isApproved && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">ปฏิเสธแผน (ถ้าต้องการ)</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                เหตุผลในการปฏิเสธ
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows="3"
                placeholder="กรุณากรอกเหตุผลในการปฏิเสธแผน..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* Approved Info (if approved) */}
        {isApproved && plan.approvedAt && (
          <div className="bg-green-50 rounded-lg shadow-sm border border-green-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-green-800 mb-2">ข้อมูลการอนุมัติ</h2>
            <div className="space-y-2 text-sm">
              {plan.approvedBy && (
                <p className="text-green-700">
                  อนุมัติโดย: <span className="font-medium">{approverName || plan.approvedBy}</span>
                </p>
              )}
              {plan.approvedAt && (
                <p className="text-green-700">
                  อนุมัติเมื่อ: <span className="font-medium">
                    {plan.approvedAt?.toDate?.().toLocaleString('th-TH') || 
                     plan.approvedAt?.seconds ? new Date(plan.approvedAt.seconds * 1000).toLocaleString('th-TH') :
                     'N/A'}
                  </span>
                </p>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate('/nurse/shifts/plans')}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
          >
            {isApproved ? 'กลับ' : 'ยกเลิก'}
          </button>
          {!isApproved && canApprove && (
            <>
              <button
                onClick={handleReject}
                disabled={rejecting || approving}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {rejecting ? 'กำลังปฏิเสธ...' : 'ปฏิเสธ'}
              </button>
              <button
                onClick={handleApprove}
                disabled={approving || rejecting || submittedMembers.length === 0}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {approving ? 'กำลังอนุมัติ...' : 'อนุมัติแผน'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewShiftPlanPage;

